let getCallbacks = () => null;
let getMountedViewName = () => null;
let setSuppressNextRowClick = () => {};
let scheduleRender = () => {};
let uiState = null;

let desktopViewTouchState = null;
let desktopViewTouchTapTarget = null;

export function configureDesktopPreviewTouch(deps) {
  getCallbacks = deps.getCallbacks || getCallbacks;
  getMountedViewName = deps.getMountedViewName || getMountedViewName;
  setSuppressNextRowClick = deps.setSuppressNextRowClick || setSuppressNextRowClick;
  scheduleRender = deps.scheduleRender || scheduleRender;
  uiState = deps.uiState || uiState;
}

function getDesktopViewWorld(viewName) {
  const desktopEl = document.getElementById(`${viewName}-view`);
  if (!desktopEl) return null;
  return desktopEl.querySelector(viewName === "mindmap" ? "#m-world" : "#b-world");
}

function getDesktopViewCanvas(viewName) {
  const desktopEl = document.getElementById(`${viewName}-view`);
  if (!desktopEl) return null;
  return desktopEl.querySelector(viewName === "mindmap" ? "#m-canvas" : "#b-canvas");
}

function readCurrentDesktopTransform(viewName) {
  // Source of truth is the live world transform (kept in sync by the view's
  // updateTransform). Parse it back so we don't depend on view-internal state
  // accessors.
  const world = getDesktopViewWorld(viewName);
  if (!world) return { panX: 0, panY: 0, zoom: 1 };
  const t = world.style.transform || "";
  const tx = /translate\(([-\d.]+)px,\s*([-\d.]+)px\)/.exec(t);
  const sc = /scale\(([-\d.]+)\)/.exec(t);
  return {
    panX: tx ? parseFloat(tx[1]) : 0,
    panY: tx ? parseFloat(tx[2]) : 0,
    zoom: sc ? parseFloat(sc[1]) : 1,
  };
}

export function handleDesktopViewTouchStart(event) {
  const mountedViewName = getMountedViewName();
  if (!mountedViewName) return;
  const desktopEl = document.getElementById(`${mountedViewName}-view`);
  if (!desktopEl) return;
  if (!desktopEl.contains(event.target)) return;
  // Don't intercept if the touch lands on an interactive node element.
  if (event.target.closest("button, input, .mm-toggle, .bubble-inline-badge")) return;
  // Tap on a node -> switch to Outline, focus that node. Use touchend without
  // movement to detect tap (set up here, finalized in handleDesktopViewTouchEnd).
  const nodeEl = event.target.closest?.(".mm-node, .bubble");
  if (nodeEl) {
    desktopViewTouchTapTarget = nodeEl.dataset.id || null;
  } else {
    desktopViewTouchTapTarget = null;
  }

  const canvas = getDesktopViewCanvas(mountedViewName);
  if (!canvas) return;
  const startTransform = readCurrentDesktopTransform(mountedViewName);
  const rect = canvas.getBoundingClientRect();

  if (event.touches.length === 1) {
    const t = event.touches[0];
    desktopViewTouchState = {
      mode: "pan",
      viewName: mountedViewName,
      startTransform,
      startX: t.clientX,
      startY: t.clientY,
      moved: false,
    };
  } else if (event.touches.length === 2) {
    const [a, b] = event.touches;
    const dx = b.clientX - a.clientX;
    const dy = b.clientY - a.clientY;
    const dist = Math.hypot(dx, dy);
    desktopViewTouchState = {
      mode: "pinch",
      viewName: mountedViewName,
      startTransform,
      startDist: dist,
      centerX: (a.clientX + b.clientX) / 2 - rect.left,
      centerY: (a.clientY + b.clientY) / 2 - rect.top,
      moved: false,
    };
  }
}

export function handleDesktopViewTouchMove(event) {
  if (!desktopViewTouchState) return;
  const { mode, viewName, startTransform, startX, startY, startDist, centerX, centerY } = desktopViewTouchState;
  const callbacks = getCallbacks();

  if (mode === "pan" && event.touches.length === 1) {
    const t = event.touches[0];
    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    if (!desktopViewTouchState.moved && Math.hypot(dx, dy) < 5) return;
    desktopViewTouchState.moved = true;
    event.preventDefault();
    desktopViewTouchState.lastTransform = {
      panX: startTransform.panX + dx,
      panY: startTransform.panY + dy,
      zoom: startTransform.zoom,
    };
    callbacks?.setDesktopViewTransform?.(viewName, desktopViewTouchState.lastTransform, { commitToStore: false });
  } else if (mode === "pinch" && event.touches.length === 2) {
    const [a, b] = event.touches;
    const dx = b.clientX - a.clientX;
    const dy = b.clientY - a.clientY;
    const dist = Math.hypot(dx, dy);
    const ratio = dist / startDist;
    // Down to 0.02, or to where the pinch started when a fit of a large map
    // went lower: a pinch never snaps the zoom back up and loses the framing.
    const floor = Math.min(0.02, startTransform.zoom);
    const newZoom = Math.max(floor, Math.min(3, startTransform.zoom * ratio));
    // Keep the pinch midpoint stable on screen.
    const worldX = (centerX - startTransform.panX) / startTransform.zoom;
    const worldY = (centerY - startTransform.panY) / startTransform.zoom;
    const newPanX = centerX - worldX * newZoom;
    const newPanY = centerY - worldY * newZoom;
    desktopViewTouchState.moved = true;
    event.preventDefault();
    desktopViewTouchState.lastTransform = { panX: newPanX, panY: newPanY, zoom: newZoom };
    callbacks?.setDesktopViewTransform?.(viewName, desktopViewTouchState.lastTransform, { commitToStore: false });
  }
}

export function handleDesktopViewTouchEnd(event) {
  if (!desktopViewTouchState && !desktopViewTouchTapTarget) return;
  const callbacks = getCallbacks();
  // If we panned/pinched, apply the final non-persistent preview transform and
  // swallow the synthetic click.
  if (desktopViewTouchState?.moved) {
    commitPendingDesktopViewTransform();
    event.preventDefault?.();
    setSuppressNextRowClick(true);
    desktopViewTouchTapTarget = null;
  } else if (desktopViewTouchTapTarget) {
    // Stationary tap on a node -> focus it in Outline tab.
    const nodeId = desktopViewTouchTapTarget;
    desktopViewTouchTapTarget = null;
    callbacks?.Store.setFocus?.(nodeId);
    callbacks?.Store.setSelected?.(nodeId);
    if (uiState) uiState.tab = "outline";
    scheduleRender();
    event.preventDefault?.();
    setSuppressNextRowClick(true);
  }
  desktopViewTouchState = null;
}

// Apply any in-flight pinch/pan transform to the embedded desktop preview
// without persisting it. Called from touchend, visibilitychange, and unmount so
// the preview DOM stays coherent while mobile is active, but desktop pan/zoom
// preferences are not overwritten by mobile viewing.
export function commitPendingDesktopViewTransform() {
  if (!desktopViewTouchState?.lastTransform) return;
  getCallbacks()?.setDesktopViewTransform?.(
    desktopViewTouchState.viewName,
    desktopViewTouchState.lastTransform,
    { commitToStore: false },
  );
  desktopViewTouchState.lastTransform = null;
}

export function resetDesktopViewTouchState() {
  desktopViewTouchState = null;
  desktopViewTouchTapTarget = null;
}

// Compute a fit-to-view transform on the desktop mindmap or bubble view by
// sampling the bounding box of all rendered node elements. We do not write
// world.style.transform directly; every transform change goes through the
// setDesktopViewTransform callback so the desktop view's state stays the
// single source of truth.
export function fitDesktopView(viewName) {
  const callbacks = getCallbacks();
  const desktopEl = document.getElementById(`${viewName}-view`);
  if (!desktopEl) return;
  const canvas = desktopEl.querySelector(viewName === "mindmap" ? "#m-canvas" : "#b-canvas");
  const world = desktopEl.querySelector(viewName === "mindmap" ? "#m-world" : "#b-world");
  if (!canvas || !world || !callbacks?.setDesktopViewTransform) return;

  const canvasRect = canvas.getBoundingClientRect();
  if (canvasRect.width <= 0 || canvasRect.height <= 0) return;

  // Stash the current transform via state, drop to identity to measure
  // natural extents, then apply the fit. Done through the callback so the
  // desktop view's mState/bState tracks each step.
  //
  // The world elements animate transform changes (e.g. .mm-world has a 120ms
  // transition), so the identity reset must be measured with transitions OFF
  // and a forced reflow — otherwise getBoundingClientRect samples the
  // mid-animation (i.e. old) transform and the fit frames a fraction of the
  // real content. That bug left a fully-expanded mindmap "fit" showing a
  // couple of nodes.
  const prev = readCurrentDesktopTransform(viewName);
  const prevTransition = world.style.transition;
  world.style.transition = "none";
  try {
  callbacks.setDesktopViewTransform(viewName, { panX: 0, panY: 0, zoom: 1 }, { commitToStore: false });
  void world.offsetHeight; // force reflow so the identity transform is live

  const nodes = world.querySelectorAll(viewName === "mindmap" ? ".mm-node" : ".bubble");
  if (!nodes.length) {
    // Canvas-mode bubble view builds no DOM — fit from the physics-derived
    // world bounds instead (world units equal identity-transform pixels, so
    // the math below is unchanged).
    const bounds = viewName === "bubble"
      ? callbacks?.getBubbleContentBounds?.()
      : callbacks?.getMindmapContentBounds?.();
    if (bounds) {
      const contentW = Math.max(1, bounds.maxX - bounds.minX);
      const contentH = Math.max(1, bounds.maxY - bounds.minY);
      const padding = 20;
      const fitScale = Math.min(
        (canvasRect.width - padding * 2) / contentW,
        (canvasRect.height - padding * 2) / contentH,
      );
      // As low as the content needs: a 20,000-item map is shown whole at
      // about 0.003 on a phone, under the old 0.02 floor (2026-10-15).
      const scale = Math.min(2, fitScale);
      const panX = (canvasRect.width - contentW * scale) / 2 - bounds.minX * scale;
      const panY = (canvasRect.height - contentH * scale) / 2 - bounds.minY * scale;
      callbacks.setDesktopViewTransform(viewName, { panX, panY, zoom: scale }, { commitToStore: false });
      return;
    }
    callbacks.setDesktopViewTransform(viewName, prev, { commitToStore: false });
    return;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const worldRect = world.getBoundingClientRect();
  nodes.forEach((node) => {
    const r = node.getBoundingClientRect();
    minX = Math.min(minX, r.left - worldRect.left);
    minY = Math.min(minY, r.top - worldRect.top);
    maxX = Math.max(maxX, r.right - worldRect.left);
    maxY = Math.max(maxY, r.bottom - worldRect.top);
  });
  const contentW = Math.max(1, maxX - minX);
  const contentH = Math.max(1, maxY - minY);
  const padding = 20;
  const fitScale = Math.min(
    (canvasRect.width - padding * 2) / contentW,
    (canvasRect.height - padding * 2) / contentH,
  );
  // No floor (was 0.02, before that 0.1): "fit" must actually fit — a fully
  // expanded ~700-node mindmap lays out >20k px tall and needs ~0.03 on a
  // phone, a 20,000-item bubble map about 0.003.
  const scale = Math.min(2, fitScale);
  const panX = (canvasRect.width - contentW * scale) / 2 - minX * scale;
  const panY = (canvasRect.height - contentH * scale) / 2 - minY * scale;
  callbacks.setDesktopViewTransform(viewName, { panX, panY, zoom: scale }, { commitToStore: false });
  } finally {
    // Land the final transform without animation, then restore the
    // transition for normal pan/zoom afterwards.
    void world.offsetHeight;
    world.style.transition = prevTransition;
  }
}
